import { Component, computed, effect, ElementRef, inject, input, signal, untracked, viewChild } from '@angular/core';
import { PaywallCardComponent } from "../paywall-card/paywall-card.component";
import { ButtonSelectComponent } from "../button-select/button-select.component";
import { PaywallService } from '../../services/paywall.service';
import { Charge, ChargePlan, PaywallSettings } from '../../models/paywall-settings';
import { PricePipe } from "../../pipes/price.pipe";
import { ChargePlanDataPipe } from "../../pipes/charge-plan-data.pipe";
import { PaywallQuantitySelectorComponent } from '../paywall-quantity-selector/paywall-quantity-selector.component';
import { contactSalesIncludesHtml, contactSalesTitle, getSeatBounds, isContactSalesEnabled, isQuantityChargePlan, quantityCharges, seatsHelperLabel, chargePlanDataForPlan } from '../../helpers/quantity.helper';
import { commonCurrencyCodes } from '../../helpers/pricing.helper';
@Component({
  selector: 'yon-pw-paywall-cards-view',
  imports: [
    PaywallCardComponent,
    ButtonSelectComponent,
    PricePipe,
    ChargePlanDataPipe,
    PaywallQuantitySelectorComponent
  ],
  providers: [PricePipe],
  templateUrl: './paywall-cards-view.component.html',
  styleUrl: './paywall-cards-view.component.scss'
})
export class PaywallCardsViewComponent {
  public settings = input.required<PaywallSettings | undefined>();
  public paywallService: PaywallService = inject(PaywallService);
  private readonly pricePipe = inject(PricePipe);
  public quantities = this.paywallService.cardQuantities;

  /** The row of cards, which scrolls sideways when the cards don't fit the width the paywall gets. */
  private readonly scroller = viewChild<ElementRef<HTMLElement>>('scroller');
  /** True while some cards are outside the row; shows the arrows. */
  public overflowing = signal(false);
  public canScrollBack = signal(false);
  public canScrollForward = signal(false);

  /** One card per charge plan, in product order then charge plan order. */
  public cards = computed<ChargePlan[]>(() => {
    const settings = this.settings();
    const products = settings?.products?.length
      ? settings.products
      : (settings?.product ? [settings.product] : []);
    return products.flatMap(product => product.chargePlans ?? []);
  });

  public contactSales = computed(() => {
    const data = this.settings()?.contactSalesData;
    return isContactSalesEnabled(data)
      ? {
        title: contactSalesTitle(data),
        subTitle: data?.subTitle ?? '',
        includesTitle: data?.includesTitle ?? '',
        includesHtml: contactSalesIncludesHtml(data)
      }
      : undefined;
  });

  public isQuantityPaywall = computed(() =>
    this.cards().some(plan => isQuantityChargePlan(plan)));
  public includesTitle = computed<string>(() => {
    const plan = this.paywallService.selectedChargePlan();
    return chargePlanDataForPlan(plan, this.settings()?.chargePlanData)?.includesTitle || '';
  });
  public includedFeaturesText = computed<string>(() => {
    const plan = this.paywallService.selectedChargePlan();
    return (chargePlanDataForPlan(plan, this.settings()?.chargePlanData)?.includesSubTitle ?? []).join('\n');
  });

  billingPeriods = computed<any[]>(() => {

    const getBillingPeriodLabel = (period: any): string => {
      switch (period) {
        case 0:
          return 'Monthly';
        case 1:
          return 'Quarterly';
        case 2:
          return 'Annually';
        default:
          return '';
      }
    }

    return [0, 2]?.map((period) => {
      return {
        label: getBillingPeriodLabel(period),
        value: period
      };
    }) ?? [];
  });

  /** A currency is offered only when every charge on every card has a price in it. */
  currencies = computed<any[]>(() => {
    return commonCurrencyCodes(this.cards()).map((currency) => {
      return {
        id: currency,
        label: currency,
        value: currency,
        currencySymbol: currency,
        code: currency,
        description: currency
      };
    });
  });

  constructor() {
    // The row overflows depending on the paywall width and on the cards, so it is re-measured
    // whenever either the row or the cards inside it change size.
    effect(onCleanup => {
      const scroller = this.scroller()?.nativeElement;
      if (!scroller) {
        return;
      }
      const observer = new ResizeObserver(() => this.updateScrollState());
      observer.observe(scroller);
      if (scroller.firstElementChild) {
        observer.observe(scroller.firstElementChild);
      }
      onCleanup(() => observer.disconnect());
    });

    effect(() => {
      // Keep the buyer's currency when they come back from checkout; fall back to the first one offered.
      const currencies = this.currencies();
      const current = untracked(() => this.paywallService.selectedCurrency()?.code);
      if (currencies.length > 0 && !currencies.some(currency => currency.code === current)) {
        this.paywallService.selectedCurrency.set(currencies[0]);
      }
    });

    // Seat bounds depend on the currency, so quantities are (re)clamped whenever the cards or the
    // currency change; a card without a quantity yet starts at its default.
    effect(() => {
      const plans = this.cards();
      this.paywallService.selectedCurrency();
      untracked(() => {
        const current = this.quantities();
        const next = { ...current };
        let changed = false;
        for (const plan of plans) {
          const bounds = this.boundsFor(plan);
          const quantity = next[plan.id] == null
            ? bounds.initial
            : clampQuantity(next[plan.id], bounds.min, bounds.max);
          if (quantity !== next[plan.id]) {
            next[plan.id] = quantity;
            changed = true;
          }
        }
        if (changed) {
          this.quantities.set(next);
        }

        const selected = this.paywallService.selectedChargePlan();
        if (selected && isQuantityChargePlan(selected)) {
          const bounds = this.boundsFor(selected);
          const quantity = clampQuantity(this.paywallService.selectedQuantity(), bounds.min, bounds.max);
          if (quantity !== this.paywallService.selectedQuantity()) {
            this.paywallService.selectedQuantity.set(quantity);
          }
        }
      });
    });
  }

  updateScrollState(): void {
    const scroller = this.scroller()?.nativeElement;
    if (!scroller) {
      return;
    }
    // A pixel of slack absorbs the fractional scroll positions of zoomed or high-DPI pages.
    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    this.overflowing.set(maxScroll > 1);
    this.canScrollBack.set(scroller.scrollLeft > 1);
    this.canScrollForward.set(scroller.scrollLeft < maxScroll - 1);
  }

  /** Moves the row one card back (-1) or forward (1); scroll snapping lines the card up. */
  scrollCards(direction: -1 | 1): void {
    const scroller = this.scroller()?.nativeElement;
    const cards = scroller?.firstElementChild?.children;
    if (!scroller || !cards?.length) {
      return;
    }
    const step = cards.length > 1
      ? (cards[1] as HTMLElement).offsetLeft - (cards[0] as HTMLElement).offsetLeft
      : scroller.clientWidth;
    scroller.scrollBy({ left: direction * step, behavior: 'smooth' });
  }

  selectBillingPeriod(billingPeriod: any) {
    this.paywallService.selectedBillingPeriod.set(billingPeriod);
  }

  selectCurrency(currency: any) {
    this.paywallService.selectedCurrency.set(currency);
  }

  selectChargePlan(chargePlan: ChargePlan) {
    this.paywallService.setSelection(chargePlan, this.quantityFor(chargePlan));
    this.paywallService.persistSelection();
    this.paywallService.step.set('checkout');
  }

  selectContactSales(): void {
    this.paywallService.step.set('contact');
  }

  isQuantityPlan(plan: ChargePlan): boolean {
    return isQuantityChargePlan(plan);
  }

  boundsFor(plan: ChargePlan) {
    const data = chargePlanDataForPlan(plan, this.settings()?.chargePlanData);
    return getSeatBounds(plan, data, this.paywallService.selectedCurrency()?.code);
  }

  quantityFor(plan: ChargePlan): number {
    return this.quantities()[plan.id] ?? this.boundsFor(plan).initial;
  }

  setQuantity(plan: ChargePlan, quantity: number): void {
    this.quantities.update(current => ({ ...current, [plan.id]: quantity }));
  }

  seatsHelper(plan: ChargePlan): string {
    const bounds = this.boundsFor(plan);
    return seatsHelperLabel(bounds.min, bounds.max, bounds.step);
  }

  quantityChargesOf(plan: ChargePlan): Charge[] {
    return quantityCharges(plan);
  }

  unitLabel(charge: Charge): string {
    return this.pricePipe.unitLabel(charge);
  }

  chargeLabel(charge: Charge, plan: ChargePlan): string {
    return this.pricePipe.chargeLabel(charge, this.quantityFor(plan));
  }

  hasOneTime(plan: ChargePlan): boolean {
    return this.pricePipe.hasOneTime(plan);
  }

  oneTimeLabel(plan: ChargePlan): string {
    return this.pricePipe.oneTimeLabel(plan, this.quantityFor(plan));
  }

}

function clampQuantity(quantity: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, quantity));
}
